// Captures Google Ads click identifiers from the landing URL so a later subscription purchase can
// be attributed server-side. Identifiers are held in memory before a consent choice and are only
// persisted or returned after an explicit advertising-measurement grant.

import {
  getConsentSnapshot,
  hasAdsMeasurementConsent,
  subscribeToConsentChanges,
  type ConsentSnapshot,
} from '@services/consent';

const STORAGE_KEY = 'saf:ad-click-ids';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_CLICK_ID_LENGTH = 512;

export interface AdClickIds {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
}

interface StoredAdClickIds extends AdClickIds {
  capturedAt: number;
}

let pendingClickIds: StoredAdClickIds | null = null;
let unsubscribeFromConsent: (() => void) | null = null;

function normaliseClickId(value: string | null): string | undefined {
  const normalised = value?.trim();
  if (!normalised || normalised.length > MAX_CLICK_ID_LENGTH) {
    return undefined;
  }
  return normalised;
}

function removeStoredClickIds(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage is best-effort, while the in-memory consent boundary remains enforced.
  }
}

function persistPendingClickIds(): void {
  if (!pendingClickIds || !hasAdsMeasurementConsent()) {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingClickIds));
    pendingClickIds = null;
  } catch {
    // Private mode or quota failure: retain only for this page lifetime.
  }
}

function applyConsent(snapshot: ConsentSnapshot): void {
  if (snapshot.hasDecision && snapshot.preferences.adsMeasurement) {
    persistPendingClickIds();
    return;
  }

  removeStoredClickIds();
  if (snapshot.hasDecision) {
    // A rejection or withdrawal makes the pre-choice identifier ineligible for later replay.
    pendingClickIds = null;
  }
}

function ensureConsentSubscription(): void {
  if (!unsubscribeFromConsent) {
    unsubscribeFromConsent = subscribeToConsentChanges(applyConsent);
  }
}

/**
 * Captures click ids before SPA navigation can remove the query string. Before a decision they stay
 * in module memory only. A rejection discards them; a grant persists them for server attribution.
 */
export function captureAdClickIds(): void {
  if (typeof window === 'undefined') {
    return;
  }

  ensureConsentSubscription();
  const snapshot = getConsentSnapshot();
  applyConsent(snapshot);

  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = normaliseClickId(params.get('gclid'));
    const gbraid = normaliseClickId(params.get('gbraid'));
    const wbraid = normaliseClickId(params.get('wbraid'));
    if (!gclid && !gbraid && !wbraid) {
      return;
    }

    if (snapshot.hasDecision && !snapshot.preferences.adsMeasurement) {
      // The click arrived under a current rejection; never retain it for a future preference change.
      return;
    }

    pendingClickIds = { gclid, gbraid, wbraid, capturedAt: Date.now() };
    if (snapshot.hasDecision && snapshot.preferences.adsMeasurement) {
      persistPendingClickIds();
    }
  } catch {
    // Attribution is non-critical and never affects product startup.
  }
}

/** Returns eligible click ids only while advertising-measurement consent is currently granted. */
export function getAdClickIds(): AdClickIds | null {
  if (!hasAdsMeasurementConsent()) {
    return null;
  }

  if (pendingClickIds && Date.now() - pendingClickIds.capturedAt <= MAX_AGE_MS) {
    const { gclid, gbraid, wbraid } = pendingClickIds;
    return { gclid, gbraid, wbraid };
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredAdClickIds | null;
    if (
      !parsed ||
      typeof parsed.capturedAt !== 'number' ||
      Date.now() - parsed.capturedAt > MAX_AGE_MS
    ) {
      removeStoredClickIds();
      return null;
    }

    const gclid = normaliseClickId(parsed.gclid ?? null);
    const gbraid = normaliseClickId(parsed.gbraid ?? null);
    const wbraid = normaliseClickId(parsed.wbraid ?? null);
    if (!gclid && !gbraid && !wbraid) {
      removeStoredClickIds();
      return null;
    }
    return { gclid, gbraid, wbraid };
  } catch {
    return null;
  }
}

/** Test-only reset for module state; not used by production code. */
export function resetAdAttributionForTests(): void {
  unsubscribeFromConsent?.();
  unsubscribeFromConsent = null;
  pendingClickIds = null;
}
